import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../Messages';
import { formatAppDate } from '../../utils/dateUtils';
import { showApiError } from '../../utils/errorUtils';
import '../../pages/Home/WarehouseDashboard.css';

// Quick-add measurement names: rolls (width, boxes) and inks (viscosity, shade)
const PRESETS = ['Ancho (mm)', 'Cajas', 'Viscosidad', 'Tono'];

const STATUS_CLASS = { PASSED: 'status-success', QUARANTINE: 'status-warning', FAILED: 'status-error' };
const CONFIRM_LABEL = { PASSED: 'Approve', QUARANTINE: 'Keep in Quarantine', FAILED: 'Reject' };

const inputStyle = {
  width: '100%',
  padding: '0.6rem',
  borderRadius: '4px',
  border: '1px solid var(--border)',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  fontSize: '0.95rem',
  fontFamily: 'inherit'
};

const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-primary)' };

const toRows = (measurements) => {
  const entries = Object.entries(measurements || {});
  return entries.length ? entries.map(([name, value]) => ({ name, value: String(value ?? '') })) : [{ name: '', value: '' }];
};

/**
 * QC decision on a received purchase order line (GRN item): accept, keep in quarantine or reject.
 * Accepting puts the quantity in stock; quarantine and rejection keep it out (#280).
 * Shared by Inventory → Receiving → Quality Check and Quality Control → Receipts.
 */
const GRNInspectionModal = ({ item, onClose, onSaved }) => {
  const { t } = useTranslation('qc');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();
  const [status, setStatus] = useState(item.quality_status || 'PASSED');
  const [notes, setNotes] = useState('');
  const [defects, setDefects] = useState(item.defects_found || '');
  const [rows, setRows] = useState(toRows(item.measurements));
  const [saving, setSaving] = useState(false);

  const setRow = (index, field, value) =>
    setRows(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  const addRow = (name = '') => {
    if (name && rows.some(r => r.name === name)) return;
    setRows(prev => {
      const empty = prev.findIndex(r => !r.name && !r.value);
      if (name && empty >= 0) return prev.map((r, i) => (i === empty ? { ...r, name } : r));
      return [...prev, { name, value: '' }];
    });
  };

  const measurements = () => {
    const out = {};
    rows.forEach(({ name, value }) => {
      if (!name.trim() || value === '') return;
      const n = Number(value);
      out[name.trim()] = value.trim() !== '' && !Number.isNaN(n) ? n : value;
    });
    return Object.keys(out).length ? out : null;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/grn-items/${item.grn_item_id}/quality-inspection`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quality_status: status,
          inspection_notes: notes || null,
          defects_found: defects || null,
          measurements: measurements()
        })
      });
      if (response.ok) {
        showSuccess(t('receipts.messages.savedTitle', 'QC decision saved'), t(`receipts.decision.${status}`, status));
        if (onSaved) onSaved(await response.json());
        onClose();
      } else {
        await showApiError(response, showError, tCommon, t('receipts.messages.saveFailed', 'Could not save the QC decision'));
      }
    } catch (error) {
      console.error('Error saving QC decision:', error);
      showError(tCommon('ui.error'), t('receipts.messages.saveFailed', 'Could not save the QC decision'));
    } finally {
      setSaving(false);
    }
  };

  const qty = `${parseFloat(item.quantity_received).toLocaleString()} ${item.unit_of_measure || ''}`;

  return (
    <div className="modal-overlay inspection-modal-overlay" onClick={onClose}>
      <div className="modal-content history-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2>{t('receipts.modal.title', 'Receipt quality check')}</h2>
          <button className="modal-close" onClick={onClose}>&#x2715;</button>
        </div>

        <div className="modal-material-info">
          <div className="material-info-row">
            <span className="info-label">{t('receipts.fields.grn', 'GRN')}:</span>
            <span className="info-value"><strong>{item.grn_number}</strong></span>
            <span className="info-label">{t('receipts.fields.po', 'PO')}:</span>
            <span className="info-value">{item.po_number}</span>
          </div>
          <div className="material-info-row">
            <span className="info-label">{t('receipts.fields.material', 'Material')}:</span>
            <span className="info-value"><strong>{item.item_code}</strong> - {item.item_name}</span>
          </div>
          <div className="material-info-row">
            <span className="info-label">{t('receipts.fields.quantity', 'Quantity')}:</span>
            <span className="info-value">{qty}</span>
            <span className="info-label">{t('receipts.fields.location', 'Location')}:</span>
            <span className="info-value">{item.location || '-'}</span>
          </div>
          <div className="material-info-row">
            <span className="info-label">{t('receipts.fields.supplier', 'Supplier')}:</span>
            <span className="info-value">{item.supplier_name || '-'}</span>
            <span className="info-label">{t('receipts.fields.receiptDate', 'Received')}:</span>
            <span className="info-value">{item.receipt_date ? formatAppDate(item.receipt_date) : '-'}</span>
          </div>
          <div className="material-info-row">
            <span className="info-label">{t('receipts.fields.currentStatus', 'Current status')}:</span>
            <span className="info-value">
              <span className={`status-badge ${STATUS_CLASS[item.quality_status] || 'status-pending'}`}>
                {t(`receipts.status.${item.quality_status}`, item.quality_status)}
              </span>
            </span>
          </div>
          {item.damage_notes && (
            <div className="material-info-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
              <span className="info-label">{t('receipts.fields.previousNotes', 'Previous notes')}:</span>
              <span className="info-value" style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem', padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '4px', width: '100%' }}>
                {item.damage_notes}
              </span>
            </div>
          )}
        </div>

        <div className="modal-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={labelStyle}>{t('receipts.modal.decision', 'Decision')}</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
                <option value="PASSED">{t('receipts.decision.PASSED', 'Accept - goes into stock')}</option>
                <option value="QUARANTINE">{t('receipts.decision.QUARANTINE', 'Quarantine - not in stock, pending review')}</option>
                <option value="FAILED">{t('receipts.decision.FAILED', 'Reject - not in stock')}</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>{t('receipts.modal.measurements', 'Measurements')}</label>
              {rows.map((row, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    style={{ ...inputStyle, flex: 2 }}
                    value={row.name}
                    placeholder={t('receipts.modal.measurementName', 'Measure (e.g. Width mm)')}
                    onChange={(e) => setRow(index, 'name', e.target.value)}
                  />
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={row.value}
                    placeholder={t('receipts.modal.measurementValue', 'Value')}
                    onChange={(e) => setRow(index, 'value', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-action btn-secondary"
                    onClick={() => setRows(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : [{ name: '', value: '' }]))}
                    title={t('receipts.modal.removeMeasurement', 'Remove')}
                  >
                    &#x2715;
                  </button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {PRESETS.map(name => (
                  <button key={name} type="button" className="btn-action btn-secondary" onClick={() => addRow(name)}>+ {name}</button>
                ))}
                <button type="button" className="btn-action btn-secondary" onClick={() => addRow()}>
                  + {t('receipts.modal.otherMeasurement', 'Other')}
                </button>
              </div>
            </div>

            <div>
              <label style={labelStyle}>{t('receipts.modal.defects', 'Defects found')}</label>
              <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={defects} onChange={(e) => setDefects(e.target.value)} />
            </div>

            <div>
              <label style={labelStyle}>{t('receipts.modal.notes', 'Inspection notes')}</label>
              <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-action btn-secondary" onClick={onClose}>{t('receipts.modal.cancel', 'Cancel')}</button>
          <button
            className={`btn-action ${status === 'PASSED' ? 'btn-success' : status === 'FAILED' ? 'btn-danger' : 'btn-warning'}`}
            onClick={submit}
            disabled={saving}
          >
            {t(`receipts.modal.confirm.${status}`, CONFIRM_LABEL[status])}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GRNInspectionModal;
